import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaApple, FaGooglePlay, FaGift, FaPhone, FaFacebook, FaTwitter, FaWhatsapp, FaShare } from 'react-icons/fa';
import { referralService } from '../../services/referralService';
import vacuumCleaner from '../../assets/imgs/vacun.jpg';
import logo from '../../assets/imgs/apple-touch-icon.png';
import LogoLoader from "../Common/LogoLoader";

const ReferralLanding = () => {
  const { referralId } = useParams();
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [referrer, setReferrer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isInvalidLink, setIsInvalidLink] = useState(false);

  useEffect(() => {
    const fetchReferrer = async () => {
      try {
        // referralId can be either a user ID or a referral code
        const response = await referralService.getReferrerById(referralId);
        setReferrer(response.data.referrer);
      } catch (error) {
        console.error('Error fetching referrer:', error);
        setIsInvalidLink(true);
      } finally {
        setLoading(false);
      }
    };

    if (referralId) {
      fetchReferrer();
    }
  }, [referralId]);

  const handlePhoneSubmit = async (store) => {
    // Clear any previous messages first
    setError('');
    setSuccess('');

    // Validate phone number first - this should prevent any API calls if empty
    if (!phoneNumber || !phoneNumber.trim()) {
      setError('Phone number is required');
      return;
    }

    // Basic phone number validation
    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{10,}$/;
    if (!phoneRegex.test(phoneNumber.trim())) {
      setError('Please enter a valid phone number');
      return;
    }

    // If we reach here, phone number is valid
    setSubmitting(true);

    try {
      // Get the referrer ID (in case referralId is a code, we need the actual user ID)
      let referrerId = referralId;
      if (referrer && referrer._id) {
        referrerId = referrer._id;
      }

      const response = await referralService.trackReferralClick(referrerId, phoneNumber);
      
      // Check if phone is already registered
      if (response.data?.code === 'PHONE_REGISTERED') {
        setError('This phone number is already registered. Please log in instead.');
        setSubmitting(false);
        return;
      }

      setSuccess('Thank you! Redirecting to download...');
      
      // Redirect to store after a short delay
      setTimeout(() => {
        if (store === 'apple') {
          window.open('https://apps.apple.com/app/jhonks/id123456789', '_blank');
        } else {
          window.open('https://play.google.com/store/apps/details?id=com.jhonks.jhonksmobile', '_blank');
        }
      }, 1500);
    } catch (error) {
      console.error('Error tracking referral:', error);
      // Only show invalid referral link error if it's actually a 404 from the API
      if (error.response?.status === 404) {
        setIsInvalidLink(true);
      } else if (error.response?.data?.code === 'PHONE_REGISTERED') {
        setError('This phone number is already registered. Please log in instead.');
      } else {
        setError(error.response?.data?.message || 'Failed to process referral. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const shareToSocial = (platform) => {
    const shareUrl = window.location.href;
    const shareText = "Join Jhonks and get a free vacuum cleaner! Start selling your waste materials and earn money today.";
    
    let url = '';
    
    switch (platform) {
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'whatsapp':
        url = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
        break;
      default:
        // Native sharing
        if (navigator.share) {
          navigator.share({
            title: 'Get a Free Vacuum Cleaner - Join Jhonks',
            text: shareText,
            url: shareUrl,
          });
          return;
        }
        // Fallback to copying to clipboard
        navigator.clipboard.writeText(shareUrl);
        setSuccess('Link copied to clipboard!');
        setTimeout(() => setSuccess(''), 3000);
        return;
    }
    
    if (url) {
      window.open(url, '_blank', 'width=600,height=400');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center">
        <LogoLoader label="Opening your invite" />
      </div>
    );
  }

  if (isInvalidLink) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Referral Link</h1>
          <p className="text-gray-600 mb-6">This referral link is not valid or has expired.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-6 rounded-lg transition-colors"
          >
            Go to Jhonks Homepage
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100">
      {/* Meta tags for social sharing */}
      <head>
        <title>Get a Free Vacuum Cleaner - Join Jhonks with {referrer?.firstName}</title>
        <meta name="description" content="Join Jhonks and get a free vacuum cleaner! Start selling your waste materials and earn money today." />
        <meta property="og:title" content="Get a Free Vacuum Cleaner - Join Jhonks" />
        <meta property="og:description" content="Join Jhonks and get a free vacuum cleaner! Start selling your waste materials and earn money today." />
        <meta property="og:image" content="/assets/vacuum-cleaner-promo.jpg" />
        <meta property="og:url" content={`${window.location.origin}/referral/${referralId}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Get a Free Vacuum Cleaner - Join Jhonks" />
        <meta name="twitter:description" content="Join Jhonks and get a free vacuum cleaner! Start selling your waste materials and earn money today." />
        <meta name="twitter:image" content="/assets/vacuum-cleaner-promo.jpg" />
      </head>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
  <img
    src={logo}
    alt="Gift"
    className="w-10 h-10 object-contain"
  />
</div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Get a Free Vacuum Cleaner!
            </h1>
            <p className="text-xl text-gray-600 mb-2">
              Join Jhonks and start earning money from your waste materials
            </p>
            {referrer && (
              <p className="text-lg text-green-600 font-semibold">
                Invited by {referrer.firstName} {referrer.lastName}
              </p>
            )}
          </div>

          {/* Main Content */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Side - Image and Benefits */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <div className="text-center">
                  <img
                    src={vacuumCleaner}
                    alt="Free Vacuum Cleaner"
                    className="w-full h-64 object-cover rounded-lg mb-6"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjI0MCIgdmlld0JveD0iMCAwIDMyMCAyNDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMjQwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xNjAgODBDMTgwIDgwIDE5NiA5NiAxOTYgMTE2VjE2MEMxOTYgMTgwIDE4MCAxOTYgMTYwIDE5NkMxNDAgMTk2IDEyNCAxODAgMTI0IDE2MFYxMTZDMTI0IDk2IDE0MCA4MCAxNjAgODBaIiBmaWxsPSIjMTA5NzY1Ii8+CjxwYXRoIGQ9Ik0xNDAgMTAwSDE4MFYxNDBIMTQwVjEwMFoiIGZpbGw9IiNGRkZGRkYiLz4KPC9zdmc+';
                    }}
                  />
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Free Vacuum Cleaner
                  </h2>
                  <p className="text-gray-600">
                  Get a vacuum cleaner to remove dirt and other debris from carpets, hard floors, and other surfaces in your home.
                  </p>
                </div>
              </div>

              {/* Benefits */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-gray-900">Why Join Jhonks?</h3>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <p className="text-gray-600">Turn your waste into cash</p>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <p className="text-gray-600">Help protect the environment</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <p className="text-gray-600">Easy pickup and payment</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Phone Form */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="text-center mb-6">
                <FaPhone className="text-3xl text-green-600 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Get Started Now
                </h2>
                <p className="text-gray-600">
                  Enter your phone number to download the app and claim your rewards
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-start animate-pulse shadow-md">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium">{error}</p>
                    {error === 'Phone number is required' && (
                      <p className="text-xs text-red-600 mt-1">
                        Please enter your phone number before downloading the app.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {success && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium">{success}</p>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    value={phoneNumber}
                    onChange={(e) => {
                      setPhoneNumber(e.target.value);
                      setError(''); // Clear error when user starts typing
                    }}
                    placeholder="e.g., +234 801 234 5678"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 transition-colors ${
                      error === 'Phone number is required' || error === 'Please enter a valid phone number'
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50'
                        : 'border-gray-300 focus:ring-green-500 focus:border-green-500'
                    }`}
                    required
                  />
                </div>

                <div className="space-y-3">
                  {error && error.includes('already registered') ? (
                    <div className="w-full bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg text-center">
                      <p className="font-medium">This number is already registered</p>
                      <p className="text-sm mt-1">Please log in to your account instead</p>
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => handlePhoneSubmit('apple')}
                        disabled={submitting || (error && error.includes('already registered'))}
                        className={`w-full font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center space-x-3 ${
                          submitting || (error && error.includes('already registered'))
                            ? 'bg-gray-400 cursor-not-allowed text-white' 
                            : 'bg-black hover:bg-gray-800 text-white'
                        }`}
                      >
                        <FaApple className="text-xl" />
                        <span>{submitting ? 'Processing...' : 'Download for iOS'}</span>
                      </button>

                      <button
                        onClick={() => handlePhoneSubmit('android')}
                        disabled={submitting || (error && error.includes('already registered'))}
                        className={`w-full font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center space-x-3 ${
                          submitting || (error && error.includes('already registered'))
                            ? 'bg-gray-400 cursor-not-allowed text-white' 
                            : 'bg-green-600 hover:bg-green-700 text-white'
                        }`}
                      >
                        <FaGooglePlay className="text-xl" />
                        <span>{submitting ? 'Processing...' : 'Download for Android'}</span>
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Your referral will be approved after the person you referred sells at least 5kg of products on Jhonks.
                </p>
              </div> */}

              {/* Social Sharing */}
              <div className="mt-6">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Share this offer</h3>
                <div className="flex space-x-3">
                  <button
                    onClick={() => shareToSocial('facebook')}
                    className="flex items-center justify-center w-10 h-10 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    title="Share on Facebook"
                  >
                    <FaFacebook />
                  </button>
                  <button
                    onClick={() => shareToSocial('twitter')}
                    className="flex items-center justify-center w-10 h-10 bg-blue-400 text-white rounded-lg hover:bg-blue-500 transition-colors"
                    title="Share on Twitter"
                  >
                    <FaTwitter />
                  </button>
                  <button
                    onClick={() => shareToSocial('whatsapp')}
                    className="flex items-center justify-center w-10 h-10 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                    title="Share on WhatsApp"
                  >
                    <FaWhatsapp />
                  </button>
                  <button
                    onClick={() => shareToSocial('native')}
                    className="flex items-center justify-center w-10 h-10 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    title="Share"
                  >
                    <FaShare />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-12">
            <p className="text-gray-500 text-sm">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-green-600 hover:text-green-700 font-medium"
              >
                Sign in here
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralLanding;
